import { Maybe, Unionize } from "fond/types";

/**
 * Constants for the various response codes that can be returned by AWS Cognito.
 */
export const CognitoResponseCode = {
  UNKNOWN_ERROR: "UnknownError",
  NETWORK_ERROR: "NetworkError",
  INCORRECT_PASSWORD: "NotAuthorizedException",
  USER_NOT_FOUND: "UserNotFoundException",
  NEW_PASSWORD_REQUIRED: "NEW_PASSWORD_REQUIRED",
  EXPIRED_CODE: "ExpiredCodeException",
  INCORRECT_CODE: "CodeMismatchException",
  LIMIT_EXCEEDED: "LimitExceededException",
  USERNAME_EXISTS_EXCEPTION: "UsernameExistsException",
  INVALID_PARAMETER: "InvalidParameterException",
  INVALID_PASSWORD_EXCEPTION: "InvalidPasswordException",
  USER_NOT_CONFIRMED: "UserNotConfirmedException",
} as const;

const CognitoResponseCodes = Object.values(CognitoResponseCode);
export type CognitoResponseCode = Unionize<typeof CognitoResponseCodes>;

/**
 * Return the helper text based on the cognito response code.
 * @param cognito response code
 * @returns helper text
 */
export function getCognitoResponseHelperText(cognitoResponseCode?: Maybe<CognitoResponseCode>): string {
  if (cognitoResponseCode === CognitoResponseCode.USER_NOT_FOUND) {
    return "Couldn't find an account for this email";
  }

  if (isUserNotConfirmed(cognitoResponseCode)) {
    return "This email hasn't been verified";
  }

  if (cognitoResponseCode === CognitoResponseCode.INCORRECT_PASSWORD || cognitoResponseCode === CognitoResponseCode.INVALID_PASSWORD_EXCEPTION) {
    return "Incorrect password. Try again or click Forgot password to reset.";
  }

  return "";
}

/**
 * Check the cognito response code to see if it is a unverified user error.
 * @param cognito response code
 */
export function isUserNotConfirmed(cognitoResponseCode?: Maybe<CognitoResponseCode>): boolean {
  const unconfirmedCodes: CognitoResponseCode[] = [CognitoResponseCode.INVALID_PARAMETER, CognitoResponseCode.USER_NOT_CONFIRMED];
  return !!(cognitoResponseCode && unconfirmedCodes.includes(cognitoResponseCode));
}

/**
 * Build a TOTP otpauth:// URI for use in QR codes and authenticator apps.
 * All parameters are URI-encoded to handle special characters in email addresses.
 * The issuer includes the environment name (e.g. "FOND (Staging)") so users can
 * distinguish between MFA entries for different environments in their authenticator app.
 */
export function buildTotpUri(email: string, secret: string): string {
  const stage = import.meta.env.REACT_APP_STAGE;
  const issuer = stage && stage !== "production" && stage !== "demo" ? `FOND (${stage})` : "FOND";
  return `otpauth://totp/${encodeURIComponent(`${issuer}:${email}`)}?secret=${encodeURIComponent(secret)}&issuer=${encodeURIComponent(issuer)}`;
}

export type CognitoResponseError = {
  code: CognitoResponseCode;
  name: string;
  message: string;
};

/**
 * Check whether a given error matches the shape of a Cognito error response
 * @param err - the error
 */
export const isCognitoError = (err: unknown): err is CognitoResponseError => {
  if (err && typeof err === "object" && "code" in err && typeof err.code === "string") {
    if (CognitoResponseCodes.includes(err.code as CognitoResponseCode)) {
      return true;
    }
  }
  return false;
};

/**
 * Checks that a password matches the password policy. Currently this is minimum 8 chars.
 * This is also enforced by cognito.
 * @param password - a password
 * @returns whether the password complies with the policy
 */
export function isValidPassword(password: Maybe<string>): boolean {
  return Boolean(password && password.length >= 8);
}
